import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import deepmerge from 'deepmerge';
import { Box, IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

import useNotification from '../../../core/hooks/use_notification';
import { getPodsNotificationData } from '../../../core/utils/notification_utils';
import PaginationControls from '../../common/pagination_controls';
import { getDefaultPodsPaginationQueryValues } from '../../../core/utils/api_utils';
import { getPodsAction, getPodsState } from '../../../features/pod/get_pods';
import { GET_ROUTE_POD_SALES_DISTRIBUTION, GET_ROUTE_PODS_EDIT, ROUTE_PODS_NEW } from '../../../core/constants';
import { Button } from "../../common/button";

const initialQueryParams = {
    ...getDefaultPodsPaginationQueryValues
};

const PodsList = ({ tab }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();

    const getPods = useSelector(getPodsState);

    const [queryParams, setQueryParams] = useState(initialQueryParams);

    useEffect(() => {
        dispatch(getPodsAction(queryParams))
            .then(response => showErrorMessage(getPodsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 2 } }>
            <Box sx={ { display: 'flex', justifyContent: 'flex-end' } }>
                <Button onClick={ () => navigate(ROUTE_PODS_NEW, { state: { tab } }) }>
                    Dodaj punkt sprzedaży
                </Button>
            </Box>

            { getPods?.loading && <LinearProgress/> }

            { getPods?.data?.content && (
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>Partner</TableCell>
                                    <TableCell>Urządzenie</TableCell>
                                    <TableCell>Opis</TableCell>
                                    <TableCell>Adres</TableCell>
                                    <TableCell align="center">Typ</TableCell>
                                    <TableCell align="center" sx={ { width: 100 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    getPods?.data?.content?.map(pod => (
                                        <TableRow key={ pod?.id } hover>
                                            <TableCell>{ pod?.indoorPartner?.name }</TableCell>
                                            <TableCell>{ pod?.device?.serviceId || '-' }</TableCell>
                                            <TableCell>{ pod?.description }</TableCell>
                                            <TableCell>{ pod?.address }</TableCell>
                                            <TableCell align="center">{ pod?.locationType }</TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" justifyContent="center" spacing={ 1 }>
                                                    <IconButton
                                                        size="small"
                                                        title="Rozkład sprzedaży"
                                                        onClick={ () => navigate(GET_ROUTE_POD_SALES_DISTRIBUTION(pod?.id)) }
                                                    >
                                                        <QueryStatsIcon fontSize="small"/>
                                                    </IconButton>
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        title="Edytuj"
                                                        onClick={ () => navigate(GET_ROUTE_PODS_EDIT(pod?.id), { state: { tab } }) }
                                                    >
                                                        <EditIcon fontSize="small"/>
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                                { (!getPods?.data?.content || getPods?.data?.content.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={ 6 } align="center"
                                                   sx={ { py: 4, color: 'text.secondary' } }>
                                            Brak wyników do wyświetlenia.
                                        </TableCell>
                                    </TableRow>
                                ) }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                        <PaginationControls
                            currentPage={ getPods?.data?.pageable?.pageNumber }
                            totalPages={ getPods?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default PodsList;